import { useInterval } from 'ahooks'
import { Divider } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import EditIcon from '@/assets/icons/edit.svg?react'
import { Button, Loading, message, Trans } from '@/components'
import { PatchOrderType } from '@/consts/order'
import { useAppContext } from '@/hooks/useAppContext'
import { MachineBill } from '@/models/bill'
import { Instance } from '@/models/instance'
import { formatDatePreset, formatMillicentPrice, formatPrice } from '@/utils/formatter'

import { ExpirationBehaviorModal } from './ExpirationBehaviorModal'
import { UpdateToggle } from './UpdateToggle'

interface InstanceBillingProps {
  instance: Instance
  teamResID?: string
  onInstanceUpdated?: () => void | Promise<void>
  allowSubscriptionActions?: boolean
}

export function InstanceBilling({
  instance,
  teamResID,
  onInstanceUpdated,
  allowSubscriptionActions = true,
}: InstanceBillingProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isOnTeamPage } = useAppContext()
  const [bills, setBills] = useState<MachineBill[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string>()
  const [changingOrder, setChangingOrder] = useState(false)
  const [expirationModalOpen, setExpirationModalOpen] = useState(false)

  const getData = useCallback(async () => {
    try {
      const response = teamResID
        ? await API.getTeamMemberInstanceBills(
            { res_id: teamResID, inst_res_id: instance.resID },
            { noErrMsg: true },
          )
        : await API.getNodeBill({ request_id: instance.displayID }, { noErrMsg: true })

      if (response.code !== 0) {
        setLoadError(
          response.msg ||
            t('instance.detail.billing.loadFailed', {
              defaultValue: 'Unable to load billing details.',
            }),
        )
        return
      }

      setBills(response.bills.map((bill) => new MachineBill(bill)))
      setLoadError(undefined)
    } catch (error) {
      console.error(error)
      setLoadError(
        t('instance.detail.billing.loadFailed', {
          defaultValue: 'Unable to load billing details.',
        }),
      )
    } finally {
      setLoading(false)
    }
  }, [instance.displayID, instance.resID, t, teamResID])

  useInterval(() => void getData(), instance.isReleased ? undefined : 10000, { immediate: true })

  useEffect(() => {
    if (instance.isReleased) void getData()
  }, [getData, instance.isReleased])

  const handleAutoRenewalChange = async (autoRenewal: boolean) => {
    if (!instance.order || changingOrder || !allowSubscriptionActions) return
    setChangingOrder(true)

    try {
      const response = await API.updateOrder(
        {
          type: PatchOrderType.AutoRenew,
          from_order_id: instance.order.id,
          auto_renewal: autoRenewal,
        },
        { noErrMsg: true },
      )

      if (response.code !== 0) {
        message.error(
          response.msg ||
            t('instance.detail.billing.updateFailed', {
              defaultValue: 'Unable to update the subscription.',
            }),
        )
        return
      }

      message.success(t('common.message.success'))
      await onInstanceUpdated?.()
    } catch (error) {
      console.error(error)
      message.error(
        t('instance.detail.billing.updateFailed', {
          defaultValue: 'Unable to update the subscription.',
        }),
      )
    } finally {
      setChangingOrder(false)
    }
  }

  const handleExpirationBehaviorChange = async (switchOn: boolean) => {
    if (!instance.order || changingOrder || !allowSubscriptionActions) return
    setChangingOrder(true)

    try {
      const response = await API.updateOrder(
        {
          type: PatchOrderType.OrderToBilling,
          from_order_id: instance.order.id,
          switch_on: switchOn,
        },
        { noErrMsg: true },
      )

      if (response.code !== 0) {
        message.error(
          response.msg ||
            t('instance.detail.billing.updateFailed', {
              defaultValue: 'Unable to update the subscription.',
            }),
        )
        return
      }

      setExpirationModalOpen(false)
      message.success(t('common.message.success'))
      await onInstanceUpdated?.()
    } catch (error) {
      console.error(error)
      message.error(
        t('instance.detail.billing.updateFailed', {
          defaultValue: 'Unable to update the subscription.',
        }),
      )
    } finally {
      setChangingOrder(false)
    }
  }

  return (
    <div className="flex flex-col gap-y-4 rounded-lg bg-panel-gray-100 py-4 t-sm text-tertiary">
      {loading ? (
        <div className="flex w-full items-center justify-center py-4">
          <Loading />
        </div>
      ) : null}

      {!loading && loadError ? <div className="px-4 text-error">{loadError}</div> : null}

      {!loading
        ? bills.map((bill, index) => (
            <div key={`${bill.displayID}-${index}`} className="grid w-full grid-cols-5 gap-y-4">
              <div className="px-4">
                <div className="text-secondary">
                  <Trans i18nKey="instance.detail.billing.startTime">Start Time</Trans>
                </div>
                <div className="text-primary">{formatDatePreset(bill.startTime, 'minute')}</div>
              </div>
              <div className="px-4">
                <div className="text-secondary">
                  <Trans i18nKey="instance.detail.billing.endTime">End Time</Trans>
                </div>
                <div className="text-primary">
                  {bill.isRunning ? (
                    <span className="text-success">
                      <Trans i18nKey="instance.detail.billing.running">Running</Trans>
                    </span>
                  ) : (
                    formatDatePreset(bill.endTime, 'minute')
                  )}
                </div>
              </div>
              <div className="px-4">
                <div className="text-secondary">
                  {bill.billType === 'Hourly' ? (
                    <Trans i18nKey="instance.detail.billing.priceHour">Price/Hour</Trans>
                  ) : bill.billType === 'Daily' ? (
                    <Trans i18nKey="instance.detail.billing.priceDay">Price/Day</Trans>
                  ) : bill.billType === 'Monthly' ? (
                    <Trans i18nKey="instance.detail.billing.priceMonth">Price/Month</Trans>
                  ) : (
                    <Trans i18nKey="instance.detail.billing.price">Price</Trans>
                  )}
                </div>
                <div className="text-primary">
                  {formatMillicentPrice(bill.priceMillicent, false)}
                </div>
              </div>
              <div className="px-4">
                <div className="text-secondary">
                  <Trans i18nKey="instance.detail.billing.cost">Cost</Trans>
                </div>
                <div className="text-primary">{formatPrice(Math.abs(bill.amount), false)}</div>
              </div>
              <div className="px-4">
                <div className="text-secondary">
                  <Trans i18nKey="instance.detail.billing.duration">Duration</Trans>
                </div>
                <div className="text-primary">{bill.usedTimeStr}</div>
              </div>
              {allowSubscriptionActions ? (
                <>
                  <div className="px-4">
                    <div className="text-secondary">
                      <Trans i18nKey="instance.detail.billing.billingMethod">Billing Method</Trans>
                    </div>
                    <div className="text-primary">
                      {bill.billingMethodLabel === 'Subscription' ? (
                        <Trans i18nKey="instance.detail.billing.subscription">Subscription</Trans>
                      ) : (
                        <Trans i18nKey="instance.detail.billing.payAsYouGo">Pay-as-you-go</Trans>
                      )}
                    </div>
                  </div>
                  <div className="px-4">
                    <div className="text-secondary">
                      <Trans i18nKey="instance.detail.billing.discount">Discount</Trans>
                    </div>
                    <div className="text-primary">
                      {bill.discountLabel === 'No Discount' ? (
                        <Trans i18nKey="instance.detail.billing.noDiscount">No Discount</Trans>
                      ) : (
                        <Trans i18nKey="instance.detail.billing.discount">Discount</Trans>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ))
        : null}

      {allowSubscriptionActions && instance.isSubscription && instance.canRelease ? (
        <div className="grid grid-cols-5 gap-y-4">
          <div className="px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.billing.autoRenewal">Auto Renewal</Trans>
            </div>
            <UpdateToggle
              type="autoRenewal"
              value={instance.autoRenewal}
              disabled={changingOrder}
              onChange={handleAutoRenewalChange}
            />
          </div>

          {!instance.autoRenewal ? (
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.expirationBehavior">
                  Expiration Behavior
                </Trans>
              </div>
              <div className="flex items-start gap-1 text-primary">
                {instance.orderToBilling ? (
                  <Trans i18nKey="instance.detail.billing.switchToPayAsYouGo">
                    Switch to pay-as-you-go
                  </Trans>
                ) : (
                  <Trans i18nKey="instance.detail.billing.releaseInstance">Release Instance</Trans>
                )}
                <Button
                  aria-label={t('instance.detail.billing.editExpirationBehavior', {
                    defaultValue: 'Edit expiration behavior',
                  })}
                  disabled={changingOrder}
                  className="p-0"
                  onClick={() => setExpirationModalOpen(true)}
                >
                  <EditIcon className="h-5 w-5 text-highlight" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {allowSubscriptionActions ? (
        <div className="px-4">
          <Divider className="mb-4" />
          <button
            type="button"
            className="t-x-sb text-btn-secondary"
            onClick={() =>
              navigate(isOnTeamPage ? '/team/billing?catalog=1' : '/billing?catalog=1')
            }
          >
            <Trans i18nKey="instance.detail.billing.viewBillingHistory">View Billing History</Trans>
          </button>
        </div>
      ) : null}

      <ExpirationBehaviorModal
        open={expirationModalOpen}
        value={instance.orderToBilling}
        submitting={changingOrder}
        onClose={() => setExpirationModalOpen(false)}
        onConfirm={handleExpirationBehaviorChange}
      />
    </div>
  )
}
